import * as React from 'react';
import { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { supabase } from '../../lib/supabase';
import { findQrSession, markQrSessionUsed } from '../../db/qrSessionRepository';
import SignupForm from '../../components/forms/SignupForm';

export default function Signup() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const navigate = useNavigate();
  const { t } = useTranslation();

  const handleSignup = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    setSuccess(false);

    if (password !== confirmPassword) {
      setError(t('auth.password_mismatch'));
      setLoading(false);
      return;
    }

    if (password.length < 6) {
      setError(t('auth.password_too_short'));
      setLoading(false);
      return;
    }

    try {
      const { data, error } = await supabase.auth.signUp({
        email,
        password,
      });

      if (error) throw error;

      if (data.user) {
        // 대기 중인 QR 토큰이 있는지 확인 (QR 스캔 후 회원가입한 경우)
        const pendingToken = localStorage.getItem('pending_qr_token');
        if (pendingToken) {
          localStorage.removeItem('pending_qr_token');
          
          // QR 세션 유효성 확인 후 연결
          const session = await findQrSession(pendingToken);

          if (session && !session.is_used && new Date(session.expires_at) > new Date()) {
            await markQrSessionUsed(pendingToken, data.user.id);

            localStorage.setItem('qr_auto_play', 'true');
            localStorage.setItem('qr_session_token', pendingToken);
            localStorage.setItem('qr_user_id', data.user.id);
            navigate('/dashboard?mode=remote');
            return;
          }
        }

        setSuccess(true);
        setTimeout(() => {
          navigate('/login');
        }, 2000);
      }
    } catch (err: any) {
      setError(err.message || t('auth.signup_failed'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-teal-50 to-cyan-50 flex items-center justify-center px-4">
      <div className="w-full max-w-md">
        <div className="bg-white rounded-2xl shadow-xl p-8">
          <div className="text-center mb-8">
            <div className="w-16 h-16 bg-gradient-to-br from-teal-500 to-cyan-500 rounded-2xl flex items-center justify-center mx-auto mb-4">
              <i className="ri-user-add-line text-3xl text-white"></i>
            </div>
            <h1 className="text-3xl font-bold text-gray-900 mb-2">{t('auth.signup_title')}</h1>
            <p className="text-gray-600">{t('auth.signup_subtitle')}</p>
          </div>

          <SignupForm
            email={email}
            password={password}
            confirmPassword={confirmPassword}
            loading={loading}
            success={success}
            error={error || undefined}
            onChangeEmail={setEmail}
            onChangePassword={setPassword}
            onChangeConfirmPassword={setConfirmPassword}
            onSubmit={handleSignup}
          />

          <div className="mt-6 text-center">
            <p className="text-gray-600 text-sm">
              {t('auth.has_account')}{' '}
              <Link to="/login" className="text-teal-600 font-medium hover:text-teal-700">
                {t('auth.login_link')}
              </Link>
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
